// Progress tracking helpers — highlight rules, history log, role+assignment permissions.

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function parseDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

/**
 * Compute deadline/status highlight for a sub.
 * Levels: GREEN (selesai tepat waktu) | YELLOW (deadline dekat < 7 hari & progress < 80%) |
 *         RED (lewat deadline & progress < 100) | DONE_LATE (selesai tapi terlambat) |
 *         NORMAL (tidak ada tanda khusus)
 */
export function computeHighlight(sub) {
  const today = startOfDay(new Date());
  const target = parseDate(sub.targetSelesai);
  const selesai = parseDate(sub.selesaiAktual);
  const progress = sub.progress || 0;

  if (sub.status === "SELESAI") {
    if (selesai && target) {
      return selesai <= target
        ? { level: "GREEN", label: "Tepat Waktu" }
        : { level: "DONE_LATE", label: "Selesai Terlambat" };
    }
    return { level: "GREEN", label: "Selesai" };
  }

  if (!target) return { level: "NORMAL", label: null };

  const msPerDay = 1000 * 60 * 60 * 24;
  const daysLeft = Math.floor((target.getTime() - today.getTime()) / msPerDay);

  if (daysLeft < 0 && progress < 100) {
    return {
      level: "RED",
      label: `Lewat ${Math.abs(daysLeft)} hari`,
      daysLeft,
    };
  }
  if (daysLeft <= 7 && progress < 80) {
    return {
      level: "YELLOW",
      label: daysLeft <= 0 ? "Hari terakhir" : `${daysLeft} hari lagi`,
      daysLeft,
    };
  }

  return { level: "NORMAL", label: null, daysLeft };
}

export const HIGHLIGHT_STYLES = {
  RED: {
    border: "border-l-4 border-l-red-500",
    badge: "bg-red-100 text-red-800 border-red-200",
  },
  YELLOW: {
    border: "border-l-4 border-l-amber-400",
    badge: "bg-amber-100 text-amber-800 border-amber-200",
  },
  GREEN: {
    border: "border-l-4 border-l-emerald-500",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
  },
  DONE_LATE: {
    border: "border-l-4 border-l-slate-400",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
  },
  NORMAL: {
    border: "",
    badge: "",
  },
};

// ---------- Permissions ----------
export function computePermissions(user, sub, assignment) {
  const base = {
    canInputProgress: false,
    canVerify: false,
    canAddKoordinasi: false,
    readOnly: true,
    reason: null,
  };
  if (!user || !sub) return base;
  if (sub.specialType === "AUDIT_INVESTIGASI") {
    return { ...base, reason: "Kegiatan Audit Investigasi dikelola di modul Phase 6." };
  }

  const isAnggota =
    Array.isArray(assignment?.anggotaIds) && assignment.anggotaIds.includes(user.id);
  const isKetua = assignment?.ketuaId === user.id;

  switch (user.role) {
    // ADMIN merangkap SEMUA peran sekaligus — Irban, Dalnis, Ketua Tim, dan
    // Anggota Tim. Karena itu ia boleh input progress, verifikasi, sekaligus
    // menambah catatan koordinasi pada sub-kegiatan mana pun, tanpa harus
    // terdaftar sebagai dalnis/ketua/anggota pada penugasannya.
    case "ADMIN":
      return {
        canInputProgress: true,
        canVerify: true,
        canAddKoordinasi: true,
        readOnly: false,
        reason: null,
      };
    case "IRBAN":
      return { ...base, readOnly: true, reason: "Irban: akses hanya baca." };
    case "DALNIS":
      return {
        ...base,
        canAddKoordinasi: true,
        readOnly: true,
        reason: "Dalnis: tambah catatan koordinasi.",
      };
    case "KETUA_TIM":
      if (isKetua) {
        return {
          canInputProgress: true,
          canVerify: true,
          canAddKoordinasi: false,
          readOnly: false,
          reason: null,
        };
      }
      return {
        ...base,
        readOnly: true,
        reason: "Anda bukan Ketua Tim untuk sub-kegiatan ini.",
      };
    case "ANGGOTA_TIM":
      if (isAnggota) {
        return {
          canInputProgress: true,
          canVerify: false,
          canAddKoordinasi: false,
          readOnly: false,
          reason: null,
        };
      }
      return {
        ...base,
        readOnly: true,
        reason: "Anda tidak ditugaskan pada sub-kegiatan ini.",
      };
    default:
      return base;
  }
}

// ---------- History ----------
export function newHistoryEntry({ user, action, catatan = "" }) {
  return {
    id:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `hx-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    userId: user?.id || null,
    userName: user?.name || "—",
    userRole: user?.role || null,
    action,
    catatan,
  };
}

export function appendHistory(sub, entry) {
  const prev = Array.isArray(sub.history) ? sub.history : [];
  return { ...sub, history: [entry, ...prev] };
}

// ---------- Summary ----------
export function summarizeHighlights(subs) {
  const c = { RED: 0, YELLOW: 0, GREEN: 0, DONE_LATE: 0, NORMAL: 0 };
  for (const s of subs) {
    const h = computeHighlight(s);
    c[h.level] = (c[h.level] || 0) + 1;
  }
  return c;
}

export function formatDateShort(iso) {
  const d = parseDate(iso);
  if (!d) return "—";
  return d.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatDateTime(iso) {
  const d = parseDate(iso);
  if (!d) return "—";
  return d.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
